import React, { useState } from "react";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import {
  ChevronUp,
  ChevronDown,
  Trash2,
  Plus,
  X,
  CheckCircle,
  Circle
} from "lucide-react";

export default function ExerciseEditor({
  exercise,
  index,
  totalExercises,
  onChange,
  onMoveUp,
  onMoveDown,
  onDelete,
  t
}) {
  const [expanded, setExpanded] = useState(true);

  const handleFieldChange = (field, value) => {
    onChange({ ...exercise, [field]: value });
  };

  const handleConfigChange = (field, value) => {
    onChange({
      ...exercise,
      config: { ...exercise.config, [field]: value }
    });
  };

  // For single_choice: manage choices array
  const addChoice = () => {
    const choices = exercise.config?.choices || [];
    const newId = String.fromCharCode(97 + choices.length); // a, b, c, d...
    handleConfigChange("choices", [
      ...choices,
      { id: newId, text: "", is_correct: false }
    ]);
  };

  const updateChoice = (choiceIndex, field, value) => {
    const choices = [...(exercise.config?.choices || [])];
    choices[choiceIndex] = { ...choices[choiceIndex], [field]: value };

    // If setting this as correct, unset others
    if (field === "is_correct" && value === true) {
      choices.forEach((c, i) => {
        if (i !== choiceIndex) c.is_correct = false;
      });
    }

    handleConfigChange("choices", choices);
  };

  const removeChoice = (choiceIndex) => {
    const choices = (exercise.config?.choices || []).filter((_, i) => i !== choiceIndex);
    handleConfigChange("choices", choices);
  };

  // For dropdown: manage blanks array
  const addBlank = () => {
    const blanks = exercise.config?.blanks || [];
    const newBlank = {
      id: `b${blanks.length + 1}`,
      options: [
        { id: "o1", text: "", is_correct: true },
        { id: "o2", text: "", is_correct: false }
      ]
    };
    handleConfigChange("blanks", [...blanks, newBlank]);
  };

  const updateBlankOption = (blankIndex, optionIndex, field, value) => {
    const blanks = [...(exercise.config?.blanks || [])];
    blanks[blankIndex].options[optionIndex] = {
      ...blanks[blankIndex].options[optionIndex],
      [field]: value
    };

    // If setting as correct, unset others in same blank
    if (field === "is_correct" && value === true) {
      blanks[blankIndex].options.forEach((opt, i) => {
        if (i !== optionIndex) opt.is_correct = false;
      });
    }

    handleConfigChange("blanks", blanks);
  };

  const addBlankOption = (blankIndex) => {
    const blanks = [...(exercise.config?.blanks || [])];
    const optionNum = blanks[blankIndex].options.length + 1;
    blanks[blankIndex].options.push({
      id: `o${optionNum}`,
      text: "",
      is_correct: false
    });
    handleConfigChange("blanks", blanks);
  };

  const removeBlankOption = (blankIndex, optionIndex) => {
    const blanks = [...(exercise.config?.blanks || [])];
    blanks[blankIndex].options = blanks[blankIndex].options.filter((_, i) => i !== optionIndex);
    handleConfigChange("blanks", blanks);
  };

  const removeBlank = (blankIndex) => {
    const blanks = (exercise.config?.blanks || []).filter((_, i) => i !== blankIndex);
    handleConfigChange("blanks", blanks);
  };

  // For input: manage accepted_answers
  const addAcceptedAnswer = () => {
    const answers = exercise.config?.accepted_answers || [];
    handleConfigChange("accepted_answers", [...answers, ""]);
  };

  const updateAcceptedAnswer = (answerIndex, value) => {
    const answers = [...(exercise.config?.accepted_answers || [])];
    answers[answerIndex] = value;
    handleConfigChange("accepted_answers", answers);
  };

  const removeAcceptedAnswer = (answerIndex) => {
    const answers = (exercise.config?.accepted_answers || []).filter((_, i) => i !== answerIndex);
    handleConfigChange("accepted_answers", answers);
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "input": return t?.contentCreator?.typeInput || "Texteingabe";
      case "single_choice": return t?.contentCreator?.typeSingleChoice || "Single Choice";
      case "dropdown": return t?.contentCreator?.typeDropdown || "Dropdown/Lückentext";
      default: return type;
    }
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case "input": return "primary";
      case "single_choice": return "success";
      case "dropdown": return "warning";
      default: return "secondary";
    }
  };

  return (
    <Card className="mb-3 border">
      <Card.Header
        className="bg-light d-flex justify-content-between align-items-center py-2"
        style={{ cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-secondary">{index + 1}</span>
          <Badge bg={getTypeBadgeColor(exercise.type)}>{getTypeLabel(exercise.type)}</Badge>
          <span className="fw-medium text-truncate" style={{ maxWidth: "250px" }}>
            {exercise.title || (t?.contentCreator?.untitledExercise || "Unbenannte Übung")}
          </span>
        </div>
        <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={onMoveUp}
            disabled={index === 0}
          >
            <ChevronUp size={14} />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={onMoveDown}
            disabled={index === totalExercises - 1}
          >
            <ChevronDown size={14} />
          </Button>
          <Button variant="outline-danger" size="sm" onClick={onDelete}>
            <Trash2 size={14} />
          </Button>
        </div>
      </Card.Header>

      {expanded && (
        <Card.Body>
          {/* Common Fields */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t?.contentCreator?.exerciseId || "Übungs-ID"}</Form.Label>
                <Form.Control
                  type="text"
                  value={exercise.id || ""}
                  onChange={(e) => handleFieldChange("id", e.target.value)}
                  placeholder="z.B. M1_L1_U1"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t?.contentCreator?.exerciseType || "Typ"}</Form.Label>
                <Form.Select
                  value={exercise.type || "input"}
                  onChange={(e) => {
                    const newType = e.target.value;
                    const newExercise = { ...exercise, type: newType };

                    // Initialize config based on type
                    if (newType === "input") {
                      newExercise.config = {
                        answer: "",
                        answer_format: "number",
                        tolerance: 0,
                        unit: "€",
                        accepted_answers: []
                      };
                    } else if (newType === "single_choice") {
                      newExercise.config = {
                        choices: [
                          { id: "a", text: "", is_correct: true },
                          { id: "b", text: "", is_correct: false }
                        ]
                      };
                    } else if (newType === "dropdown") {
                      newExercise.config = {
                        text_segments: [],
                        blanks: [{
                          id: "b1",
                          options: [
                            { id: "o1", text: "", is_correct: true },
                            { id: "o2", text: "", is_correct: false }
                          ]
                        }]
                      };
                    }

                    onChange(newExercise);
                  }}
                >
                  <option value="input">{t?.contentCreator?.typeInput || "Texteingabe"}</option>
                  <option value="single_choice">{t?.contentCreator?.typeSingleChoice || "Single Choice"}</option>
                  <option value="dropdown">{t?.contentCreator?.typeDropdown || "Dropdown/Lückentext"}</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={8}>
              <Form.Group>
                <Form.Label>{t?.contentCreator?.exerciseTitle || "Titel"}</Form.Label>
                <Form.Control
                  type="text"
                  value={exercise.title || ""}
                  onChange={(e) => handleFieldChange("title", e.target.value)}
                  placeholder="z.B. Übung 1 – Grundrechnungen"
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>{t?.contentCreator?.points || "Punkte"}</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={exercise.points || 10}
                  onChange={(e) => handleFieldChange("points", parseInt(e.target.value) || 10)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>{t?.contentCreator?.timeLimit || "Zeit (s)"}</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={exercise.time_limit_seconds || 120}
                  onChange={(e) => handleFieldChange("time_limit_seconds", parseInt(e.target.value) || 120)}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>{t?.contentCreator?.prompt || "Aufgabenstellung"}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={exercise.prompt || ""}
              onChange={(e) => handleFieldChange("prompt", e.target.value)}
              placeholder={t?.contentCreator?.promptPlaceholder || "Die Frage oder Aufgabenstellung..."}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t?.contentCreator?.solution || "Lösungsweg (für Anzeige)"}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={exercise.solution || ""}
              onChange={(e) => handleFieldChange("solution", e.target.value)}
              placeholder={t?.contentCreator?.solutionPlaceholder || "Erklärung der Lösung..."}
            />
          </Form.Group>

          {/* Type-specific config */}
          <div className="border-top pt-3 mt-3">
            <h6 className="text-muted mb-3">
              {t?.contentCreator?.typeConfig || "Typ-spezifische Konfiguration"}
            </h6>

            {/* INPUT Type Config */}
            {exercise.type === "input" && (
              <>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t?.contentCreator?.correctAnswer || "Richtige Antwort"}</Form.Label>
                      <Form.Control
                        type="text"
                        value={exercise.config?.answer || ""}
                        onChange={(e) => handleConfigChange("answer", e.target.value)}
                        placeholder="z.B. 25"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t?.contentCreator?.answerFormat || "Format"}</Form.Label>
                      <Form.Select
                        value={exercise.config?.answer_format || "number"}
                        onChange={(e) => handleConfigChange("answer_format", e.target.value)}
                      >
                        <option value="number">Zahl</option>
                        <option value="text">Text</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>{t?.contentCreator?.tolerance || "Toleranz"}</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={exercise.config?.tolerance || 0}
                        onChange={(e) => handleConfigChange("tolerance", parseFloat(e.target.value) || 0)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>{t?.contentCreator?.unit || "Einheit"}</Form.Label>
                      <Form.Control
                        type="text"
                        value={exercise.config?.unit || ""}
                        onChange={(e) => handleConfigChange("unit", e.target.value)}
                        placeholder="z.B. €, %, kg"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>{t?.contentCreator?.acceptedAnswers || "Alternative Antworten"}</Form.Label>
                  {(exercise.config?.accepted_answers || []).map((answer, idx) => (
                    <div key={idx} className="d-flex gap-2 mb-2">
                      <Form.Control
                        type="text"
                        value={answer}
                        onChange={(e) => updateAcceptedAnswer(idx, e.target.value)}
                        placeholder="z.B. 25 Euro, 25€"
                      />
                      <Button variant="outline-danger" size="sm" onClick={() => removeAcceptedAnswer(idx)}>
                        <X size={14} />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline-secondary" size="sm" onClick={addAcceptedAnswer}>
                    <Plus size={14} className="me-1" />
                    {t?.contentCreator?.addAlternative || "Alternative hinzufügen"}
                  </Button>
                </Form.Group>
              </>
            )}

            {/* SINGLE_CHOICE Type Config */}
            {exercise.type === "single_choice" && (
              <Form.Group className="mb-3">
                <Form.Label>{t?.contentCreator?.choices || "Antwortmöglichkeiten"}</Form.Label>
                {(exercise.config?.choices || []).map((choice, idx) => (
                  <div key={idx} className="d-flex align-items-center gap-2 mb-2">
                    <Button
                      variant={choice.is_correct ? "success" : "outline-secondary"}
                      size="sm"
                      onClick={() => updateChoice(idx, "is_correct", !choice.is_correct)}
                      title={choice.is_correct ? "Richtige Antwort" : "Als richtig markieren"}
                    >
                      {choice.is_correct ? <CheckCircle size={14} /> : <Circle size={14} />}
                    </Button>
                    <Badge bg="light" text="dark">{choice.id})</Badge>
                    <Form.Control
                      type="text"
                      value={choice.text}
                      onChange={(e) => updateChoice(idx, "text", e.target.value)}
                      placeholder={`Antwort ${choice.id}`}
                    />
                    <Button variant="outline-danger" size="sm" onClick={() => removeChoice(idx)}>
                      <X size={14} />
                    </Button>
                  </div>
                ))}
                <Button variant="outline-secondary" size="sm" onClick={addChoice}>
                  <Plus size={14} className="me-1" />
                  {t?.contentCreator?.addChoice || "Antwort hinzufügen"}
                </Button>
              </Form.Group>
            )}

            {/* DROPDOWN Type Config */}
            {exercise.type === "dropdown" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>{t?.contentCreator?.textSegments || "Text-Segmente (zwischen Lücken)"}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={(exercise.config?.text_segments || []).join("\n")}
                    onChange={(e) => handleConfigChange("text_segments", e.target.value.split("\n"))}
                    placeholder={t?.contentCreator?.textSegmentsPlaceholder || "Ein Segment pro Zeile. Nach jedem Segment kommt eine Lücke."}
                  />
                  <Form.Text className="text-muted">
                    {t?.contentCreator?.textSegmentsHint || "Jede Zeile ist ein Textteil. Nach jedem Teil kommt ein Dropdown."}
                  </Form.Text>
                </Form.Group>

                <Form.Label>{t?.contentCreator?.blanks || "Lücken (Dropdowns)"}</Form.Label>
                {(exercise.config?.blanks || []).map((blank, blankIdx) => (
                  <Card key={blankIdx} className="mb-2 border">
                    <Card.Header className="bg-light py-2 d-flex justify-content-between align-items-center">
                      <span className="small fw-medium">
                        {t?.contentCreator?.blank || "Lücke"} {blankIdx + 1} ({blank.id})
                      </span>
                      <Button variant="outline-danger" size="sm" onClick={() => removeBlank(blankIdx)}>
                        <Trash2 size={12} />
                      </Button>
                    </Card.Header>
                    <Card.Body className="py-2">
                      {blank.options.map((option, optIdx) => (
                        <div key={optIdx} className="d-flex align-items-center gap-2 mb-2">
                          <Button
                            variant={option.is_correct ? "success" : "outline-secondary"}
                            size="sm"
                            onClick={() => updateBlankOption(blankIdx, optIdx, "is_correct", !option.is_correct)}
                          >
                            {option.is_correct ? <CheckCircle size={12} /> : <Circle size={12} />}
                          </Button>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={option.text}
                            onChange={(e) => updateBlankOption(blankIdx, optIdx, "text", e.target.value)}
                            placeholder={`Option ${optIdx + 1}`}
                          />
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeBlankOption(blankIdx, optIdx)}
                            disabled={blank.options.length <= 2}
                          >
                            <X size={12} />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={() => addBlankOption(blankIdx)}
                      >
                        <Plus size={12} className="me-1" />
                        {t?.contentCreator?.addOption || "Option"}
                      </Button>
                    </Card.Body>
                  </Card>
                ))}
                <Button variant="outline-secondary" size="sm" onClick={addBlank}>
                  <Plus size={14} className="me-1" />
                  {t?.contentCreator?.addBlank || "Lücke hinzufügen"}
                </Button>
              </>
            )}
          </div>

          {/* Tags */}
          <Form.Group className="mt-3">
            <Form.Label>{t?.contentCreator?.tags || "Tags (kommagetrennt)"}</Form.Label>
            <Form.Control
              type="text"
              value={(exercise.tags || []).join(", ")}
              onChange={(e) => handleFieldChange("tags", e.target.value.split(",").map(t => t.trim()).filter(Boolean))}
              placeholder="z.B. multiplikation, addition, alltag"
            />
          </Form.Group>
        </Card.Body>
      )}
    </Card>
  );
}
